/**
 * Artikelliste mit Suche, Filtern und Sortierung.
 *
 * Kategorien, Familien und Rollen kommen ausschliesslich aus /api/v1/meta.
 * Im Frontend steht keine dieser Listen (Auftrag Teil 6).
 *
 * Hier findet keine fachliche Berechnung statt. Die Liste stellt dar,
 * was die API liefert.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type {
  LifecycleFilter,
  StockingFilter, ArticleListItem, ArticleListResponse, ArticleOperationalFilter,
              AvailabilityFilter, MetaResponse } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { EmptyState, ErrorState, LoadingState, Unknown } from "./States.tsx";
import { FamilyBadge, FlagBadge, RoleBadge } from "./Badges.tsx";
import { verfuegbarkeitswort } from "../lib/verfuegbarkeit.ts";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { categoryLabel, familyLabel } from "../i18n/terminology.ts";
import "./ArticleList.css";

const PAGE_SIZE = 100;

interface Props {
  meta: MetaResponse;
  onOpen: (id: number) => void;
  /**
   * Verdichtete Darstellung, sobald ein Artikel geoeffnet ist.
   *
   * Die Liste traegt dann nicht mehr dieselbe Informationsdichte wie die
   * Hauptliste. Sie dient dem schnellen Wechsel -- Nummer, kurze Bezeichnung
   * und, falls vorhanden, verdichteter Handlungsbedarf. Kategorie, Familie
   * und Rollen stehen im Detail daneben; sie ein zweites Mal zu zeigen kostet
   * Platz, den das Detail besser braucht.
   */
  compact?: boolean;
  /** Der offene Artikel bleibt in der Liste erkennbar. */
  selectedId?: number | null;
  /**
   * Ein neues Produkt anlegen. `undefined`, wenn der Nutzer das nicht darf --
   * dann erscheint die Schaltflaeche gar nicht. Der Server entscheidet
   * ohnehin noch einmal.
   */
  onCreate?: () => void;
  /**
   * Den Papierkorb oeffnen.
   *
   * Er steht neben „Neues Produkt", nicht in der Hauptnavigation: ein
   * Papierkorb ist kein Arbeitsbereich, sondern ein Zustand von Produkten --
   * und er gehoert dorthin, wo man merkt, dass ein Produkt fehlt.
   */
  onOpenTrash?: () => void;
}

export function ArticleList({ meta, onOpen, compact = false, selectedId = null, onCreate,
                              onOpenTrash }: Props): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [familyId, setFamilyId] = useState<number | undefined>();
  const [roleCode, setRoleCode] = useState<string | undefined>();
  /*
   * BETRIEBSZUSTAND -- Vorgabe „aktiv".
   *
   * So steht es im Auftrag, und so bleibt es auch, wenn heute noch kein
   * Bestandsfeed angeschlossen ist: der Server setzt den Filter in diesem Fall
   * aus und SAGT es (`statusFilterSuspended`). Die Liste stellt dann den
   * ganzen Bestand mit einem erklaerenden Hinweis dar -- sie tut nicht so, als
   * waere er leer, und sie faelscht den eingestellten Filter nicht.
   */
  /*
   * DIE VORGABE IST „ALLE" -- und das ist eine Korrektur.
   *
   * Vorher stand sie auf „Aktiv" und meinte damit den Lagerbestand. Eine
   * Produktdatenliste, die Artikel ausblendet, weil ihr Regal gerade leer
   * ist, versteckt genau die, an denen zu arbeiten waere.
   */
  const [status, setStatus] = useState<AvailabilityFilter>("alle");
  const [lifecycle, setLifecycle] = useState<LifecycleFilter>("alle");
  const [stocking, setStocking] = useState<StockingFilter>("alle");
  const [operational, setOperational] = useState<ArticleOperationalFilter>("alle");
  const [sort, setSort] = useState<"article_number" | "short_description" | "category">("article_number");
  const [dir, setDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);

  const [data, setData] = useState<ArticleListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // Fehler als Code halten, nicht als fertigen Text -- so folgt er der
  // Sprachumschaltung.
  const [error, setError] = useState<unknown>(null);
  const [reload, setReload] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { setDebounced(search); setPage(1); }, 250);
    return () => clearTimeout(timer.current);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.articles({ q: debounced || undefined, categoryId, familyId, roleCode: roleCode as any,
                   availability: status, lifecycle, stocking, operational, sort, dir, page, pageSize: PAGE_SIZE })
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debounced, categoryId, familyId, roleCode, status, lifecycle, stocking, operational, sort, dir, page, reload]);

  /** Familienauswahl folgt der Kategorie -- sonst bietet sie unpassende Werte an. */
  const families = useMemo(
    () => (categoryId ? meta.families.filter((f) => f.categoryId === categoryId) : meta.families),
    [meta.families, categoryId],
  );

  /* „Aktiv" ist die VORGABE und damit kein gesetzter Filter -- sonst staende
     der Zuruecksetzen-Knopf dauerhaft da, ohne dass jemand etwas gefiltert
     haette. */
  const hasFilter = Boolean(debounced || categoryId || familyId || roleCode
                            || status !== "alle" || lifecycle !== "alle" || stocking !== "alle" || operational !== "alle");

  function resetFilters(): void {
    setSearch(""); setDebounced(""); setCategoryId(undefined);
    setFamilyId(undefined); setRoleCode(undefined);
    setStatus("alle"); setLifecycle("alle"); setStocking("alle"); setOperational("alle"); setPage(1);
  }

  function toggleSort(field: typeof sort): void {
    if (sort === field) setDir(dir === "asc" ? "desc" : "asc");
    else { setSort(field); setDir("asc"); }
    setPage(1);
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="list">
      <div className="list__toolbar">
        <input
          className="list__search"
          placeholder={t("list.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t("list.search")}
        />

        <select className="list__select" value={categoryId ?? ""} aria-label={t("list.category")}
                onChange={(e) => { const v = e.target.value; setCategoryId(v ? Number(v) : undefined);
                                   setFamilyId(undefined); setPage(1); }}>
          <option value="">{t("list.allCategories")}</option>
          {meta.categories.map((c) => (
            <option key={c.id} value={c.id}>
              {categoryLabel(i18n, c.code, c.name)} ({c.articleCount})
            </option>
          ))}
        </select>

        <select className="list__select" value={familyId ?? ""} aria-label={t("list.family")}
                onChange={(e) => { const v = e.target.value; setFamilyId(v ? Number(v) : undefined); setPage(1); }}>
          <option value="">{t("list.allFamilies")}</option>
          {families.map((f) => (
            <option key={f.id} value={f.id}>
              {familyLabel(i18n, f.code, f.name)} ({f.articleCount})
            </option>
          ))}
        </select>

        <select className="list__select" value={roleCode ?? ""} aria-label={t("list.role")}
                onChange={(e) => { const v = e.target.value; setRoleCode(v || undefined); setPage(1); }}>
          <option value="">{t("list.allRoles")}</option>
          {/* Artikelrollen sind eine Klassifikation mit festem Code. Die
              Beschriftung folgt der Sprache, der gespeicherte Code nicht. */}
          {meta.roles.map((r) => (
            <option key={r.id} value={r.code}>
              {i18n.tCode("articleRole", r.code, r.name)} ({r.articleCount})
            </option>
          ))}
        </select>

        {/*
          * DREI GETRENNTE AUSWAHLEN -- weil es drei Fragen sind.
          *
          *   Sortimentsstatus   Soll der Artikel gefuehrt werden?
          *   Verfuegbarkeit     Ist gerade Ware da?
          *   Operativ           Woraus ergibt sich das -- Lager, Zulauf,
          *                      Angebot? Mehreres zugleich ist moeglich.
          *
          * Bis zu diesem Block trug die zweite Auswahl die Woerter der ersten:
          * sie hiess „Aktiv / Inaktiv / Unbestimmt" und meinte den
          * Lagerbestand. Ein Standardartikel mit leerem Regal erschien damit
          * als „Inaktiv" -- als waere er ausgelistet.
          */}
        <select className="list__select" value={lifecycle} aria-label={t("list.lifecycle")}
                onChange={(e) => { setLifecycle(e.target.value as LifecycleFilter); setPage(1); }}>
          <option value="alle">{t("list.lifecycleAll")}</option>
          {meta.lifecycleStatuses.map((l) => (
            <option key={l.code} value={l.code}>{i18n.tCode("lifecycle", l.code, l.name)}</option>
          ))}
          <option value="offen">{t("list.lifecycleOpen")}</option>
        </select>

        <select className="list__select" value={stocking} aria-label={t("list.stocking")}
                onChange={(e) => { setStocking(e.target.value as StockingFilter); setPage(1); }}>
          <option value="alle">{t("list.stockingAll")}</option>
          {meta.stockingTypes.map((s) => (
            <option key={s.code} value={s.code}>{i18n.tCode("stocking", s.code, s.name)}</option>
          ))}
          <option value="offen">{t("list.stockingOpen")}</option>
        </select>

        <select className="list__select" value={status} aria-label={t("list.availability")}
                onChange={(e) => { setStatus(e.target.value as AvailabilityFilter); setPage(1); }}>
          <option value="alle">{t("list.availabilityAll")}</option>
          <option value="verfuegbar">{t("list.availabilityYes")}</option>
          <option value="nicht_verfuegbar">{t("list.availabilityNo")}</option>
          <option value="keine_aussage">{t("list.availabilityUnknown")}</option>
        </select>

        <select className="list__select" value={operational} aria-label={t("list.operational")}
                onChange={(e) => { setOperational(e.target.value as ArticleOperationalFilter); setPage(1); }}>
          <option value="alle">{t("list.operationalAll")}</option>
          <option value="lagerware">{t("list.operationalStock")}</option>
          <option value="zulauf">{t("list.operationalIncoming")}</option>
          <option value="angeboten">{t("list.operationalOffered")}</option>
        </select>

        {hasFilter && <button className="btn list__reset" onClick={resetFilters}>{t("list.resetFilters")}</button>}
      </div>

      {/*
        * Der ausgesetzte Statusfilter, im Klartext.
        *
        * Ohne diesen Satz saehe der Benutzer eine Liste, die „Aktiv" anzeigt
        * und alles enthaelt -- und muesste raten, ob der Filter kaputt ist.
        */}
      {data?.statusFilterSuspended && (
        <p className="list__notice">{t("list.statusSuspended")}</p>
      )}

      <div className="list__meta">
        {data && !loading && (
          <>
            <span className="list__zahl list__zahl--lang">
              <strong>{data.total}</strong> {t("list.articles")}
              {hasFilter && <> {t("list.ofTotal", { total: meta.counts.articles })}</>}
            </span>
            {/* Dieselbe Auskunft fuer eine schmale Liste -- in einer Zeile
                statt in vier. Welche der beiden steht, entscheidet die
                Breite der Liste selbst (ArticleList.css). */}
            <span className="list__zahl list__zahl--kurz">
              <strong>{data.total}</strong>
              {hasFilter ? ` / ${meta.counts.articles} ` : " "}{t("list.articles")}
            </span>
          </>
        )}
        {/* Anlegen steht bei der Liste, nicht in einem Menue: es ist der
            Gegenpol zum Suchen -- entweder man findet den Artikel, oder es
            gibt ihn noch nicht. */}
        {onCreate && (
          <button className="btn list__create" onClick={onCreate}>{t("list.createArticle")}</button>
        )}
        {onOpenTrash && (
          <button className="btn list__trash" title={t("trash.openTitle")}
            onClick={onOpenTrash}>{t("trash.open")}</button>
        )}
      </div>

      {loading && !data && <LoadingState />}
      {error !== null && <ErrorState message={errorText(i18n, error)} onRetry={() => setReload((n) => n + 1)} />}

      {error === null && data && (
        data.items.length === 0 ? (
          <EmptyState title={t("list.emptyTitle")} hint={t("list.emptyHint")} />
        ) : (
          <>
            <div className={[
                "list__table", loading ? "list__table--busy" : "", compact ? "list__table--compact" : "",
              ].filter(Boolean).join(" ")}
              aria-label={compact ? t("list.compactAria") : undefined}>
              <table>
                {/*
                  * Im verdichteten Modus ohne Tabellenkopf.
                  *
                  * Drei Spaltenueberschriften ueber zwei Angaben, von denen
                  * eine der Status war, kosteten mehr Aufmerksamkeit als sie
                  * trugen. Sortiert wird jetzt ueber die Auswahl darueber --
                  * und die Frage der Liste ist ohnehin „welchen Artikel
                  * oeffne ich", nicht „wie sortiere ich".
                  */}
                {!compact && (
                  <thead>
                    <tr>
                      <Th label={t("list.colNumber")} title={t("list.sortBy", { label: t("list.colNumber") })}
                          field="article_number" sort={sort} dir={dir} onSort={toggleSort} />
                      <Th label={t("list.colDescription")} title={t("list.sortBy", { label: t("list.colDescription") })}
                          field="short_description" sort={sort} dir={dir} onSort={toggleSort} />
                      <Th label={t("list.colCategory")} title={t("list.sortBy", { label: t("list.colCategory") })}
                          field="category" sort={sort} dir={dir} onSort={toggleSort} />
                      <th>{t("list.colFamily")}</th>
                      <th>{t("list.colRoles")}</th>
                      <th>{t("list.colStatus")}</th>
                    </tr>
                  </thead>
                )}
                <tbody>
                  {data.items.map((a) => (
                    <Row key={a.id} a={a} onOpen={onOpen} i18n={i18n}
                         compact={compact} selected={a.id === selectedId} />
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="list__pager">
                <button className="btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>{t("list.prev")}</button>
                <span>{t("list.page", { page, total: totalPages })}</span>
                <button className="btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>{t("list.next")}</button>
              </div>
            )}
          </>
        )
      )}
    </div>
  );
}

/** Dieselben drei Woerter wie im Filter -- die Liste soll nicht zweierlei heissen. */
/**
 * Die operativen Auspraegungen als ein Text.
 *
 * Sie stehen NEBENEINANDER und nicht als Rangfolge: „Lagerware · Zulauf"
 * heisst, dass beides zutrifft, und das ist eine haeufige und richtige
 * Aussage -- Ware liegt da, weitere ist bestellt.
 */
function betriebszusatz(a: ArticleListItem, t: I18n["t"]): string {
  const teile: string[] = [];
  if (a.operationalState.hasStock) teile.push(t("list.operationalStock"));
  if (a.operationalState.hasIncoming) teile.push(t("list.operationalIncoming"));
  if (a.operationalState.isOffered) teile.push(t("list.operationalOffered"));
  return teile.join(" · ");
}

function Th({ label, title, field, sort, dir, onSort }: {
  label: string; title: string; field: "article_number" | "short_description" | "category";
  sort: string; dir: string; onSort: (f: any) => void;
}): React.JSX.Element {
  const active = sort === field;
  return (
    <th>
      <button className={active ? "th-sort th-sort--active" : "th-sort"} title={title} onClick={() => onSort(field)}>
        {label}<span className="th-sort__icon">{active ? (dir === "asc" ? "↑" : "↓") : ""}</span>
      </button>
    </th>
  );
}

function Row({ a, onOpen, i18n, compact, selected }: {
  a: ArticleListItem; onOpen: (id: number) => void; i18n: I18n;
  compact: boolean; selected: boolean;
}): React.JSX.Element {
  /* Ein Platzhalter in der Artikelnummer ist bereits in der Liste sichtbar.
     Wer ihn erst im Detail bemerkt, hat ihn vorher fuer eine bestellbare SKU
     gehalten (PRODUCT_DATA_MODEL.md 3.7). */
  const platzhalter = a.numberIsPlaceholder && (
    <span className="badge badge--placeholder" title={i18n.t("models.placeholderTitle")}>
      {i18n.t("models.placeholderBadge")}
    </span>
  );

  /*
   * Verdichtete Zeile: Bezeichnung ueber zwei Zeilen, Nummer darunter.
   *
   * Vorher stand die Bezeichnung in einer einzeiligen Zelle und wurde nach
   * etwa zwanzig Zeichen mit „…" abgeschnitten -- aus „TP154 | L: 154 cm |
   * 18 - 75 W | …" liess sich kein Produkt erkennen. Zwei Zeilen reichen fuer
   * den Teil, der ein Produkt benennt.
   */
  /*
   * Verdichtete Zeile: Bezeichnung, darunter die Nummer -- und sonst nichts.
   *
   * Der Status ist ganz entfallen. „1 offen" beantwortet eine Frage, die bei
   * der AUSWAHL eines Produktes niemand stellt, und ein Warnpunkt neben jedem
   * zweiten Artikel laesst einen Bestand fehlerhaft aussehen, der es nicht
   * ist. Wo Arbeit liegt, sagt der jeweilige Arbeitsbereich.
   */
  if (compact) {
    return (
      <tr onClick={() => onOpen(a.id)} tabIndex={0}
          className={selected ? "row--selected" : undefined}
          aria-current={selected ? "true" : undefined}
          onKeyDown={(e) => { if (e.key === "Enter") onOpen(a.id); }}>
        <td className="cell-desc">
          <span className="cell-desc__name">{a.shortDescription ?? <Unknown />}</span>
          <span className="cell-desc__number">{a.articleNumber}{platzhalter}</span>
        </td>
      </tr>
    );
  }

  return (
    <tr onClick={() => onOpen(a.id)} tabIndex={0}
        className={selected ? "row--selected" : undefined}
        aria-current={selected ? "true" : undefined}
        onKeyDown={(e) => { if (e.key === "Enter") onOpen(a.id); }}>
      <td className="cell-number">
        {a.articleNumber}
        {platzhalter}
      </td>
      <td className="cell-desc">{a.shortDescription ?? <Unknown />}</td>
      {/* Der gespeicherte Wert bleibt unveraendert; uebersetzt wird nur die
          Anzeige, und das nur bei beschreibenden Begriffen. HighBay, OFFICE
          und RetroFix erscheinen in beiden Sprachen gleich. */}
      {(
        <>
          <td className="cell-cat">{categoryLabel(i18n, a.category.code, a.category.name)}</td>
          <td>
            {/* Mehrfachbeziehungen: ein Artikel, mehrere Familien -- kein Duplikat. */}
            {a.families.map((f) => (
              <FamilyBadge key={f.id} label={familyLabel(i18n, f.code, f.name)} primary={f.isPrimary} />
            ))}
          </td>
          <td>
            {/* Mehrfachrollen additiv nebeneinander. */}
            {a.roles.map((r) => <RoleBadge key={r.code} label={i18n.tCode("articleRole", r.code, r.name)} />)}
          </td>
        </>
      )}
      <td className="cell-status">
        {/*
          * EINE Zeile, keine Abzeichenwand.
          *
          * Der abgeleitete Zustand steht vorn, die operative Auspraegung als
          * ruhiger Zusatz dahinter -- „Aktiv · Lagerware", „Inaktiv ·
          * angeboten". Ist mehreres zugleich wahr, steht es nebeneinander;
          * erfunden wird nichts dazu.
          *
          * Der gepflegte Lebenszyklus ist etwas anderes als der abgeleitete
          * Zustand und steht deshalb nur da, wenn ihn jemand gesetzt hat.
          */}
        {/*
          * DER SORTIMENTSSTATUS STEHT VORN -- er ist die Aussage ueber den
          * Artikel. Die Verfuegbarkeit folgt als operative Momentaufnahme.
          *
          * Ist der Sortimentsstatus nicht gesetzt, steht das da: „nicht
          * bestimmt". Fruehere Fassungen setzten hier den abgeleiteten
          * Lagerzustand ein und behaupteten damit eine Entscheidung, die
          * niemand getroffen hat.
          */}
        <span className={`status status--lc-${a.lifecycleStatus ?? "offen"}`}>
          {a.lifecycleStatus
            ? i18n.tCode("lifecycle", a.lifecycleStatus)
            : i18n.t("list.lifecycleUnset")}
        </span>
        <span className={`status__avail status__avail--${a.operationalState.availability}`}>
          {" · "}{verfuegbarkeitswort(a.operationalState.availability, i18n.t)}
        </span>
        {/* Die Sortimentsrolle steht nur da, wenn sie entschieden ist -- ein
            „nicht bestimmt" dreimal je Zeile waere Laerm. */}
        {a.stockingType && (
          <span className="status__detail"> · {i18n.tCode("stocking", a.stockingType)}</span>
        )}
        {betriebszusatz(a, i18n.t) && (
          <span className="status__detail"> · {betriebszusatz(a, i18n.t)}</span>
        )}
        <FlagBadge count={a.openReviewFlagCount} />
      </td>
    </tr>
  );
}
